import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { kvGet, kvSet } from '@/lib/db';
import { useSyncStore } from '@/stores/syncStore';

/**
 * Reads a server-side KPI view. These are computed in Postgres, so they
 * cannot be recreated offline — the last result is cached and clearly
 * stamped instead, the same contract as the dashboard.
 */
export interface ViewResult<T> {
  rows: T[] | undefined;
  stale: boolean;
  error: string | null;
  refresh: () => void;
}

export function useView<T>(
  view: string,
  build?: (q: ReturnType<typeof supabase.from>) => unknown,
  deps: unknown[] = [],
): ViewResult<T> {
  const online = useSyncStore((s) => s.online);
  const [rows, setRows] = useState<T[] | undefined>(undefined);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const cacheKey = `view.${view}.${JSON.stringify(deps)}`;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const cached = await kvGet<T[]>(cacheKey);
      if (cached && !cancelled) {
        setRows(cached);
        setStale(true);
      }
      if (!navigator.onLine) return;

      const query = build ? build(supabase.from(view)) : supabase.from(view).select('*');
      const { data, error: err } = await (query as Promise<{ data: T[] | null; error: { message: string } | null }>);
      if (cancelled) return;

      if (err) {
        setError(err.message);
        return;
      }
      setRows((data ?? []) as T[]);
      setStale(false);
      setError(null);
      await kvSet(cacheKey, data ?? []);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, cacheKey, online, tick]);

  return { rows, stale, error, refresh: () => setTick((t) => t + 1) };
}
